import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import OpsLayout from '../components/OpsLayout';
import '../css/DoctorApplications.css';
import { API_BASE_URL } from '../config/config';
import { toast } from 'react-hot-toast';
import Modal from 'react-modal';
import { Document, Page, pdfjs } from 'react-pdf';
import { X } from 'lucide-react';
import { buildScopeParams, fetchPatientsPage, toPatientRow } from '../utils/patientsApi';
import getDashboardRiskLevel from '../utils/dashboardRiskLevel';
import RiskDot, { RiskDots } from '../components/RiskDot';

// 서버가 한 번에 100명까지만 준다(src/server/patients/validation.js). 등록 현황이 그보다 많으면 나눠 부른다.
const SIGNAL_BATCH = 100;

// 이 부분을 아래처럼 변경
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.js`;

// axios 인스턴스 생성
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

const DoctorApplications = () => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [currentDoctors, setCurrentDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [applicationPage, setApplicationPage] = useState(1);
    const [doctorPage, setDoctorPage] = useState(1);
    const [totalApplicationPages, setTotalApplicationPages] = useState(1);
    const [totalDoctorPages, setTotalDoctorPages] = useState(1);
    const [registrations, setRegistrations] = useState([]);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportPdfBase64, setReportPdfBase64] = useState(null);
    const [reportPdfName, setReportPdfName] = useState('');
    // spec D4: 대시보드와 같은 신호등. { [app_user_id]: patientRow } — 응답에 실려 온 대상자만 들어 있다.
    const [signals, setSignals] = useState({});

    useEffect(() => {
        const ids = [...new Set(
            registrations.filter((r) => r.status === 'ACCEPTED' && r.app_user_id != null).map((r) => r.app_user_id)
        )];
        if (ids.length === 0) { setSignals({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const scopeParams = buildScopeParams();
                const next = {};
                for (let i = 0; i < ids.length; i += SIGNAL_BATCH) {
                    const batch = ids.slice(i, i + SIGNAL_BATCH);
                    const page = await fetchPatientsPage({ token, scopeParams, page: 1, size: SIGNAL_BATCH, appUserIds: batch.join(',') });
                    (page.items || []).forEach((item) => {
                        const row = toPatientRow(item, { getRiskLevel: getDashboardRiskLevel });
                        next[row.app_user_id] = row;
                    });
                }
                if (!cancelled) setSignals(next);
            } catch (error) {
                // 신호등은 보조 정보다. 못 받으면 "—" 로 두고 표는 그대로 쓴다.
                console.error('주치의 등록 현황 신호등 조회 실패:', error);
                if (!cancelled) setSignals({});
            }
        })();
        return () => { cancelled = true; };
    }, [registrations]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        try {
            const decodedToken = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            
            if (decodedToken.exp < currentTime) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }

            fetchApplications(applicationPage);
            fetchCurrentDoctors(doctorPage);
            fetchRegistrations();
        } catch (error) {
            console.error('토큰 검증 실패:', error);
            navigate('/');
        }
    }, [navigate, applicationPage, doctorPage]);

    const fetchApplications = async (page) => {
        try {
            const token = localStorage.getItem('token');
            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
            let params = {
                page: page,
                limit: 10
            };

            if (isSuperAdmin) {
                params.is_super_admin = 'true';
            } else {
                const hospitalId = localStorage.getItem('hospital_id');
                if (!hospitalId) {
                    console.error('병원 ID가 없습니다.');
                    toast.error('병원 정보가 없습니다.');
                    return;
                }
                params.hospital_id = hospitalId;
            }
            
            const response = await axios.get(`${API_BASE_URL}/api/doctor-applications`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                params
            });

            if (response.data.success) {
                setApplications(response.data.applications);
                setTotalApplicationPages(response.data.totalPages);
            }
        } catch (error) {
            console.error('신청 목록 조회 실패:', error);
            toast.error('신청 목록을 불러오는데 실패했습니다.');
        }
    };

    const fetchCurrentDoctors = async (page) => {
        try {
            const token = localStorage.getItem('token');
            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
            let params = {
                page: page,
                limit: 10
            };

            if (isSuperAdmin) {
                params.is_super_admin = 'true';
            } else {
                const hospitalId = localStorage.getItem('hospital_id');
                if (!hospitalId) {
                    console.error('병원 ID가 없습니다.');
                    toast.error('병원 정보가 없습니다.');
                    return;
                }
                params.hospital_id = hospitalId;
            }
            
            const response = await axios.get(`${API_BASE_URL}/api/current-doctors`, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                params
            });

            if (response.data.success) {
                setCurrentDoctors(response.data.doctors);
                setTotalDoctorPages(response.data.totalPages);
            }
        } catch (error) {
            console.error('주치의 목록 조회 실패:', error);
            toast.error('주치의 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const fetchRegistrations = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('토큰이 없습니다.');
                return;
            }
            
            setLoading(true);
            const isSuperAdmin = localStorage.getItem('is_super_admin') === 'true';
            let params = {};

            if (isSuperAdmin) {
                params.is_super_admin = 'true';
            } else {
                const hospitalId = localStorage.getItem('hospital_id');
                if (!hospitalId) {
                    console.error('병원 ID가 없습니다.');
                    toast.error('병원 정보가 없습니다.');
                    return;
                }
                params.hospital_id = hospitalId;
            }

            const response = await api.get('/api/doctor-registrations', {
                headers: { 
                    Authorization: `Bearer ${token}`
                },
                params
            });
            
            console.log('registrations API 응답:', response.data);
            if (response.data.success) {
                setRegistrations(response.data.registrations);
            } else {
                console.error('API 응답 실패:', response.data.message);
                toast.error('데이터를 불러오는데 실패했습니다.');
            }
        } catch (error) {
            console.error('등록 테이블 조회 실패:', error);
            toast.error('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleApplicationResponse = async (applicationId, status) => {
        try {
            const token = localStorage.getItem('token');
            const requestData = { status };
            console.log('요청 데이터:', requestData);
            
            const response = await axios.put(
                `${API_BASE_URL}/api/doctor-applications/${applicationId}`,
                requestData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                toast.success(status === 'ACCEPTED' ? '신청이 수락되었습니다.' : '신청이 거절되었습니다.');
                fetchApplications(applicationPage);
                fetchCurrentDoctors(doctorPage);
                fetchRegistrations();
            }
        } catch (error) {
            console.error('신청 처리 실패:', error);
            console.error('에러 응답:', error.response?.data);
            toast.error('신청 처리에 실패했습니다.');
        }
    };

    const handleAcceptClick = (id) => {
        if (window.confirm('수락 하시겠습니까?')) {
            handleApplicationResponse(id, 'ACCEPTED');
        }
    };

    const handleDenyClick = (id) => {
        if (window.confirm('거절하시겠습니까?')) {
            handleApplicationResponse(id, 'DENIED');
        }
    };

    const openReportModal = (managementId) => {
        setReportModalOpen(true);
        setReportPdfBase64(null);
        setReportPdfName('');
        fetchHealthReport(managementId);
    };

    const fetchHealthReport = async (managementId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/health-reports`, {
                params: { management_id: managementId },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success && response.data.reports.length > 0) {
                setReportPdfBase64(response.data.reports[0].report_file_data);
                setReportPdfName(response.data.reports[0].report_filename || '건강보고서.pdf');
            } else {
                toast.error('건강보고서가 없습니다.');
            }
        } catch (e) {
            toast.error('건강보고서 조회 실패');
        }
    };

    const renderPagination = (currentPage, totalPages, onPageChange) => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const requestedRegistrations = registrations.filter((reg) => reg.status === 'REQUESTED');
    const acceptedRegistrations = registrations.filter((reg) => reg.status === 'ACCEPTED');

    return (
        <OpsLayout title="주치의 신청/조회" description="주치의 신청을 수락하거나 거절해요.">
            <section className="ops-page__card doctor-applications-page">
                <div className="ops-page__card-title">
                    <h2>주치의 신청 현황</h2>
                    {requestedRegistrations.length > 0 && (
                        <span className="sds-badge sds-badge--caution">대기 {requestedRegistrations.length}</span>
                    )}
                </div>
                <div className="sds-table-wrap">
                    <table className="sds-table">
                        <thead>
                            <tr>
                                <th>신청일자</th>
                                <th>요청자</th>
                                <th>성별</th>
                                <th>나이</th>
                                <th>주소</th>
                                <th>요청 구분</th>
                                <th className="sds-table__center">처리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requestedRegistrations.length > 0 ? (
                                requestedRegistrations.map((reg) => (
                                    <tr key={reg.id}>
                                        <td>{reg.created_at ? new Date(reg.created_at).toLocaleDateString() : '-'}</td>
                                        <td>{reg.user_name || '-'}</td>
                                        <td>{reg.gender === 'M' ? '남성' : reg.gender === 'F' ? '여성' : '-'}</td>
                                        <td>{reg.birthdate ? (new Date().getFullYear() - new Date(reg.birthdate).getFullYear()) : '-'}</td>
                                        <td>{reg.address || '-'}</td>
                                        <td>{reg.request_field || '-'}</td>
                                        <td className="sds-table__center">
                                            <div className="sds-row-actions">
                                                <button
                                                    onClick={() => handleAcceptClick(reg.id)}
                                                    className="sds-btn sds-btn--weak sds-btn--sm"
                                                >
                                                    수락
                                                </button>
                                                <button
                                                    onClick={() => handleDenyClick(reg.id)}
                                                    className="sds-btn sds-btn--secondary sds-btn--sm"
                                                >
                                                    거절
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="sds-empty">
                                        신청 내역이 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="ops-page__card doctor-applications-page">
                <div className="ops-page__card-title">
                    <h2>주치의 등록 현황</h2>
                    <small>{acceptedRegistrations.length}명</small>
                </div>
                <div className="sds-table-wrap">
                    <table className="sds-table">
                        <thead>
                            <tr>
                                <th>신청일자</th>
                                <th>수락일자</th>
                                <th>요청자</th>
                                <th>성별</th>
                                <th>나이</th>
                                <th>주소</th>
                                <th className="sds-table__center">혈압</th>
                                <th className="sds-table__center">혈당</th>
                                <th className="sds-table__center">조회</th>
                            </tr>
                        </thead>
                        <tbody>
                            {acceptedRegistrations.length > 0 ? (
                                acceptedRegistrations.map((reg) => (
                                    <tr key={reg.id}>
                                        <td>{reg.created_at ? new Date(reg.created_at).toLocaleDateString() : '-'}</td>
                                        <td>{reg.accepted_at ? new Date(reg.accepted_at).toLocaleDateString() : '-'}</td>
                                        <td>{reg.user_name || '-'}</td>
                                        <td>{reg.gender === 'M' ? '남성' : reg.gender === 'F' ? '여성' : '-'}</td>
                                        <td>{reg.birthdate ? (new Date().getFullYear() - new Date(reg.birthdate).getFullYear()) : '-'}</td>
                                        {/* 서버가 주소를 보내지 않아 늘 "-" 다(spec "목업과 다르게 하는 것" — Task 13 부채로 적는다) */}
                                        <td>{reg.address || '-'}</td>
                                        {/* spec D4: 대시보드와 같은 신호등. 응답에 없는 대상자(권한 밖·조회 실패)는 "—" 다 —
                                            회색 점은 "측정 기록 없음"이라는 뜻이라, 모르는 것을 그렇게 보이면 사실과 다르다. */}
                                        {(() => {
                                            const p = signals[reg.app_user_id];
                                            if (!p) {
                                                return (
                                                    <>
                                                        <td className="sds-table__center">—</td>
                                                        <td className="sds-table__center">—</td>
                                                    </>
                                                );
                                            }
                                            const bp = [p.bloodPressure.systolic, p.bloodPressure.diastolic];
                                            return (
                                                <>
                                                    <td className="sds-table__center">
                                                        <RiskDots items={bp.map((entry, i) => ({
                                                            level: entry ? entry.risk.level : '기록 없음',
                                                            title: entry ? `${i === 0 ? '수축기' : '이완기'}: ${entry.value} (${entry.risk.level})` : `${i === 0 ? '수축기' : '이완기'}: 기록 없음`
                                                        }))} />
                                                    </td>
                                                    <td className="sds-table__center">
                                                        <RiskDot
                                                            level={p.bloodGlucose ? p.bloodGlucose.risk.level : '기록 없음'}
                                                            title={p.bloodGlucose ? `공복혈당: ${p.bloodGlucose.value} (${p.bloodGlucose.risk.level})` : '공복혈당: 기록 없음'}
                                                        />
                                                    </td>
                                                </>
                                            );
                                        })()}
                                        <td className="sds-table__center">
                                            <button
                                                className="sds-btn sds-btn--secondary sds-btn--sm"
                                                disabled={!reg.management_id}
                                                onClick={() => reg.management_id && openReportModal(reg.management_id)}
                                            >
                                                건강보고서
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9" className="sds-empty">
                                        등록 내역이 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <Modal
                isOpen={reportModalOpen}
                onRequestClose={() => setReportModalOpen(false)}
                contentLabel="건강보고서 미리보기"
                overlayClassName="sds-modal-overlay"
                className="sds-modal sds-modal--wide"
            >
                <div className="sds-modal__head">
                    <h3 className="sds-modal__title">건강보고서{reportPdfName ? `(${reportPdfName})` : ''}</h3>
                    <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => setReportModalOpen(false)} aria-label="닫기">
                        <X size={16} aria-hidden="true" />
                    </button>
                </div>
                <div className="sds-modal__body">
                    <div className="doctor-applications-modal-pdf">
                        {reportPdfBase64 ? (
                            <Document
                                file={{ data: Uint8Array.from(atob(reportPdfBase64), c => c.charCodeAt(0)) }}
                                onLoadError={console.error}
                            >
                                <Page
                                    pageNumber={1}
                                    width={Math.min(800, window.innerWidth * 0.8)}
                                    renderTextLayer={false}
                                    renderAnnotationLayer={false}
                                />
                            </Document>
                        ) : (
                            <div className="sds-notice">PDF 데이터를 불러오는 중...</div>
                        )}
                    </div>
                </div>
            </Modal>
        </OpsLayout>
    );
};

export default DoctorApplications; 